import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell, BellRing, Heart, Clock, Calendar, Settings, LogOut,
  Radio, Trash2, Users, Sparkles, ChevronRight, X, ShieldCheck,
  Package, MapPin, ShoppingCart, MessageCircle, Gift
} from 'lucide-react';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const CATEGORIES = [
  { label: '패션', value: 'fashion' },
  { label: '뷰티', value: 'beauty' },
  { label: '푸드', value: 'food' },
  { label: '키즈', value: 'kids' },
  { label: '반려동물', value: 'pet' },
  { label: '전자제품', value: 'electronics' },
  { label: '홈', value: 'home' },
  { label: '스포츠', value: 'sports' },
  { label: '기타', value: 'others' }
];

const CATEGORY_LABEL = CATEGORIES.reduce((acc, c) => { acc[c.value] = c.label; return acc; }, {});

// Global notification preference rows (broadcast alerts + events + announcements)
const NOTIF_ROWS = [
  { key: 'today_broadcast', label: '오늘 방송' },
  { key: 'before_30min',    label: '30분 전' },
  { key: 'before_10min',    label: '10분 전' },
  { key: 'live_start',      label: 'LIVE 시작' },
  { key: 'events',          label: '이벤트' },
  { key: 'announcements',   label: '공지사항' }
];

// Notification Center categories (activity feed)
const NOTIF_CENTER_TABS = [
  { key: 'new_broadcast',  label: '새 방송' },
  { key: 'broadcast_start', label: '방송 시작' },
  { key: 'events',          label: '이벤트' },
  { key: 'announcements',   label: '공지사항' }
];

// LocalStorage helpers for "recently viewed"
const RECENT_KEY = 'livemoa_recent_broadcasts';

const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
};

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex items-center justify-between mb-5">
    <div>
      <h2 className="text-2xl font-black tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {Icon && <Icon className="text-[#FF3B30]" size={22} />}
        {title}
      </h2>
      {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-[#FF3B30]' : 'bg-gray-300 dark:bg-gray-600'
    }`}
    data-testid={testId}
    aria-checked={checked}
    role="switch"
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const MyPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [following, setFollowing] = useState([]);
  const [weekLives, setWeekLives] = useState([]);
  const [todayLives, setTodayLives] = useState([]);
  const [broadcastFavorites, setBroadcastFavorites] = useState([]);
  const [recent, setRecent] = useState([]);
  const [interested, setInterested] = useState(user?.interested_categories || []);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [globalPrefs, setGlobalPrefs] = useState(() => {
    const defaults = Object.fromEntries(NOTIF_ROWS.map((r) => [r.key, true]));
    return { ...defaults, ...(user?.global_notification_prefs || {}) };
  });
  const [notifTab, setNotifTab] = useState('new_broadcast');
  const [scheduleTab, setScheduleTab] = useState('today');
  const [savingCategories, setSavingCategories] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Cart badge for the mypage quick-link. Refreshes on mount + `livemoa:cart-updated`.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.get('/cart');
        if (!cancelled) setCartCount((r.data?.items || []).length);
      } catch { /* silent */ }
    };
    refresh();
    const onUpdated = () => refresh();
    window.addEventListener('livemoa:cart-updated', onUpdated);
    return () => { cancelled = true; window.removeEventListener('livemoa:cart-updated', onUpdated); };
  }, [user?.user_id]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
    setRecent(readRecent());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = async () => {
    try {
      const [followingRes, weekRes, todayRes, favBroadcastsRes] = await Promise.all([
        api.get('/following'),
        api.get('/lives/week'),
        api.get('/lives/today'),
        api.get('/broadcasts/favorites')
      ]);
      setFollowing(followingRes.data || []);
      setWeekLives(weekRes.data || []);
      setTodayLives(todayRes.data || []);
      setBroadcastFavorites(favBroadcastsRes.data || []);
    } catch (err) {
      console.error('Failed to load MyPage data:', err);
    }
  };

  // Followed seller live status today
  const isSellerLiveToday = (sellerId) => {
    return todayLives.some((l) => l.seller_id === sellerId);
  };
  const isSellerLiveNow = (sellerId) => {
    return todayLives.some((l) => l.seller_id === sellerId && l.status === 'live');
  };

  const followedIds = useMemo(() => new Set(following.map((s) => s.seller_id)), [following]);

  // Only include broadcasts from followed sellers
  const followedLives = useMemo(
    () => weekLives.filter((l) => followedIds.has(l.seller_id)),
    [weekLives, followedIds]
  );

  const now = new Date();
  const tabbedSchedule = useMemo(() => {
    const upcoming = followedLives.filter((l) => new Date(l.scheduled_at) >= new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    if (scheduleTab === 'today') return upcoming.filter((l) => isToday(new Date(l.scheduled_at)));
    if (scheduleTab === 'tomorrow') return upcoming.filter((l) => isTomorrow(new Date(l.scheduled_at)));
    return upcoming.filter((l) => isThisWeek(new Date(l.scheduled_at), { weekStartsOn: 1 }));
  }, [followedLives, scheduleTab, now]);

  const handleUnfollow = async (sellerId) => {
    try {
      await api.post('/unfollow', null, { params: { seller_id: sellerId } });
      setFollowing((prev) => prev.filter((s) => s.seller_id !== sellerId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveFavoriteBroadcast = async (scheduleId) => {
    try {
      await api.delete(`/broadcasts/favorite/${scheduleId}`);
      setBroadcastFavorites((prev) => prev.filter((b) => b.schedule_id !== scheduleId));
    } catch (err) {
      console.error(err);
    }
  };

  const clearRecent = () => {
    localStorage.removeItem(RECENT_KEY);
    setRecent([]);
  };

  const toggleCategory = (value) => {
    setInterested((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  };

  const saveCategories = async () => {
    if (savingCategories) return;
    setSavingCategories(true);
    try {
      await api.put('/user/interested-categories', interested);
      setUser?.((u) => u ? { ...u, interested_categories: interested } : u);
      toast.success('관심 카테고리가 저장되었습니다.');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || '저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSavingCategories(false);
    }
  };

  const savePrefs = async (nextPrefs) => {
    setSavingPrefs(true);
    try {
      await api.put('/user/global-notifications', nextPrefs);
      setUser?.((u) => u ? { ...u, global_notification_prefs: nextPrefs } : u);
      toast.success('알림 설정이 저장되었습니다.');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || '저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSavingPrefs(false);
    }
  };

  const togglePref = (key) => {
    const next = { ...globalPrefs, [key]: !globalPrefs[key] };
    setGlobalPrefs(next);
    savePrefs(next);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      setUser?.(null);
      navigate('/login');
    } catch (err) {
      console.error(err);
    }
  };

  // Derived notification center feed (from followed sellers' recent/upcoming activity)
  const notificationFeed = useMemo(() => {
    const feed = [];
    followedLives.forEach((l) => {
      if (l.status === 'live') {
        feed.push({ type: 'broadcast_start', title: `${l.seller_name} 방송이 시작되었습니다`, subtitle: l.title, time: l.scheduled_at, live: l });
      } else if (new Date(l.scheduled_at) > now) {
        feed.push({ type: 'new_broadcast', title: `${l.seller_name} 새 방송 예정`, subtitle: l.title, time: l.scheduled_at, live: l });
      }
    });
    // Static events + announcements (backend feed not implemented — client mock)
    feed.push({ type: 'events', title: 'LIVEMOA 신규 오픈 이벤트', subtitle: '팔로우 시 특별 혜택 제공 중', time: new Date().toISOString() });
    feed.push({ type: 'announcements', title: '서비스 점검 안내', subtitle: '더 나은 서비스를 위해 준비 중입니다', time: new Date().toISOString() });
    return feed.sort((a, b) => new Date(b.time) - new Date(a.time));
  }, [followedLives, now]);

  const filteredNotifs = notificationFeed.filter((n) => n.type === notifTab);

  if (!user) return null;

  const memberSince = user.created_at ? format(new Date(user.created_at), 'yyyy년 M월 d일', { locale: ko }) : '-';

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-14">
        {/* ============= USER PROFILE ============= */}
        <section data-testid="user-profile-section">
          <div className="bg-gradient-to-br from-[#FF3B30]/5 to-white dark:from-[#FF3B30]/10 dark:to-[#0A0A0A] border border-black/5 dark:border-white/10 rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <SafeImg
                src={user.picture}
                kind="avatar"
                alt={user.name}
                className="w-28 h-28 rounded-full border-4 border-white dark:border-[#0A0A0A] shadow-lg object-cover"
                data-testid="my-profile-photo"
              />
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-2">
                  <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="my-nickname">
                    {user.name}
                  </h1>
                  {/* Only FREE is shown; premium/vip are backend-only for future */}
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-black/5 dark:bg-white/10 text-xs font-bold rounded-full" data-testid="membership-badge">
                    FREE
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1" data-testid="my-email">{user.email}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="my-joined-date">가입일: {memberSince}</p>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-5">
                  <button
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-full bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                    data-testid="settings-button"
                  >
                    <Settings size={14} /> 설정
                  </button>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-full bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                    data-testid="logout-button-mypage"
                  >
                    <LogOut size={14} /> 로그아웃
                  </button>
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-4 w-full md:w-auto md:flex md:flex-col md:gap-3">
                <div className="text-center md:text-left px-4 py-2 md:py-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">팔로우</p>
                  <p className="text-2xl font-black text-[#FF3B30]" data-testid="stat-following-count">{following.length}</p>
                </div>
                <div className="text-center md:text-left px-4 py-2 md:py-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">즐겨찾기</p>
                  <p className="text-2xl font-black" data-testid="stat-favorites-count">{broadcastFavorites.length}</p>
                </div>
                <div className="text-center md:text-left px-4 py-2 md:py-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">최근 본</p>
                  <p className="text-2xl font-black" data-testid="stat-recent-count">{recent.length}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============= COMMERCE QUICK LINKS ============= */}
        <section data-testid="commerce-quick-links">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link to="/cart" className="group flex items-center gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors" data-testid="quick-link-cart">
              <div className="relative w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center">
                <ShoppingCart size={18} className="text-[#FF3B30]" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black leading-none" data-testid="quick-link-cart-badge">{cartCount}</span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm">장바구니</p>
                <p className="text-xs text-gray-500" data-testid="quick-link-cart-count">
                  {cartCount > 0 ? `담긴 상품 ${cartCount}개` : '담긴 상품 없음'}
                </p>
              </div>
              <ChevronRight size={14} className="text-gray-400 group-hover:text-[#FF3B30]" />
            </Link>
            <Link to="/orders" className="group flex items-center gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors" data-testid="quick-link-orders">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center"><Package size={18} className="text-[#FF3B30]" /></div>
              <div className="flex-1">
                <p className="font-bold text-sm">주문 내역</p>
                <p className="text-xs text-gray-500">주문 조회 · 배송 조회</p>
              </div>
              <ChevronRight size={14} className="text-gray-400 group-hover:text-[#FF3B30]" />
            </Link>
            <Link to="/my/coupons" className="group flex items-center gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors" data-testid="quick-link-coupons">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center"><Gift size={18} className="text-[#FF3B30]" /></div>
              <div className="flex-1">
                <p className="font-bold text-sm">내 쿠폰</p>
                <p className="text-xs text-gray-500">사용 가능 · 사용 완료 · 만료</p>
              </div>
              <ChevronRight size={14} className="text-gray-400 group-hover:text-[#FF3B30]" />
            </Link>
            <Link to="/addresses" className="group flex items-center gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors" data-testid="quick-link-addresses">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center"><MapPin size={18} className="text-[#FF3B30]" /></div>
              <div className="flex-1">
                <p className="font-bold text-sm">배송지 관리</p>
                <p className="text-xs text-gray-500">기본 배송지 · 신규 등록</p>
              </div>
              <ChevronRight size={14} className="text-gray-400 group-hover:text-[#FF3B30]" />
            </Link>
            <Link to="/my/inquiries" className="group flex items-center gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors" data-testid="quick-link-inquiries">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center"><MessageCircle size={18} className="text-[#FF3B30]" /></div>
              <div className="flex-1">
                <p className="font-bold text-sm">내 문의</p>
                <p className="text-xs text-gray-500">판매자 답변 확인 · 문의 이력</p>
              </div>
              <ChevronRight size={14} className="text-gray-400 group-hover:text-[#FF3B30]" />
            </Link>
            <Link to="/profile" className="group flex items-center gap-3 p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors" data-testid="quick-link-profile">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center"><Settings size={18} className="text-[#FF3B30]" /></div>
              <div className="flex-1">
                <p className="font-bold text-sm">회원정보</p>
                <p className="text-xs text-gray-500">
                  {user?.phone ? '이름 · 연락처 관리' : '연락처 등록 · 원클릭 주문·문의'}
                </p>
              </div>
              <ChevronRight size={14} className="text-gray-400 group-hover:text-[#FF3B30]" />
            </Link>
          </div>
        </section>

        {/* ============= SELLER APPLICATION STATUS ============= */}
        <section data-testid="seller-application-panel">
          {(() => {
            const status = applicationStatus?.seller_status || user?.seller_status || 'none';
            const app = applicationStatus?.application;
            if (status === 'approved') {
              // My own upcoming broadcasts (as seller) — derived from weekLives.
              const mySellerId = user?.seller_id;
              const mine = (weekLives || []).filter((l) => mySellerId && l.seller_id === mySellerId && new Date(l.scheduled_at) >= new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()));
              mine.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
              const todayMine = mine.filter((l) => isToday(new Date(l.scheduled_at)));
              const upcoming = mine[0] || null;
              return (
                <div className="border rounded-2xl p-6 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20" data-testid="seller-app-panel-approved">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck size={18} className="text-[#FF3B30]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm">판매자 승인 완료</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">판매자 대시보드에서 방송·상품·광고를 관리할 수 있습니다.</p>

                      {/* My broadcast schedule summary (approved sellers only) */}
                      <div className="mt-4 p-3 rounded-xl bg-white/70 dark:bg-black/30 border border-emerald-200/50 dark:border-emerald-500/20" data-testid="seller-my-schedule-summary">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-black tracking-wide flex items-center gap-1"><Calendar size={12} className="text-[#FF3B30]" /> 내 방송 일정</p>
                          <span className="text-[10px] font-bold text-gray-500" data-testid="seller-today-broadcast-count">오늘 예정 {todayMine.length}건</span>
                        </div>
                        {upcoming ? (
                          <div className="text-xs" data-testid="seller-nearest-broadcast">
                            <p className="font-bold truncate">{upcoming.title}</p>
                            <p className="text-gray-500 mt-0.5">
                              {format(new Date(upcoming.scheduled_at), 'yyyy.MM.dd (E) HH:mm', { locale: ko })} · <span className="capitalize">{upcoming.platform}</span>
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500" data-testid="seller-nearest-broadcast-empty">예정된 방송이 없습니다.</p>
                        )}
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <Link to="/seller/dashboard" className="inline-flex items-center gap-1 px-4 py-2 bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-bold rounded-full transition-colors" data-testid="seller-app-panel-cta">
                          판매자 대시보드 <ChevronRight size={12} />
                        </Link>
                        <Link to="/seller/dashboard?section=broadcasts" className="inline-flex items-center gap-1 px-4 py-2 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 text-xs font-bold rounded-full hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors" data-testid="seller-my-schedule-link">
                          내 방송 일정 보기 <ChevronRight size={12} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            const cfg = {
              none:      { title: '판매자로 활동하고 싶으신가요?', desc: '신청서를 제출하면 관리자 검토 후 승인 여부를 알려드립니다.', cta: '판매자 신청하기', to: '/seller/apply', style: 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10' },
              pending:   { title: '판매자 신청 심사 중', desc: '관리자가 검토 후 결과를 알려드립니다. 검토 중에는 신청서를 수정할 수 없습니다.', cta: '신청 내역 보기', to: '/seller/apply', style: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20' },
              rejected:  { title: '판매자 신청이 반려되었습니다', desc: app?.rejection_reason ? `사유: ${app.rejection_reason}` : '반려 사유를 확인 후 재신청해주세요.', cta: '재신청하기', to: '/seller/apply', style: 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20' },
              suspended: { title: '판매자 이용이 제한되었습니다', desc: '관리자 문의를 통해 이용 재개 여부를 확인해주세요.', cta: '상세 보기', to: '/seller/apply', style: 'bg-gray-100 dark:bg-white/5 border-black/10 dark:border-white/10' },
            }[status] || {};
            return (
              <div className={`border rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${cfg.style || ''}`} data-testid={`seller-app-panel-${status}`}>
                <div className="w-10 h-10 rounded-full bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={18} className="text-[#FF3B30]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">{cfg.title}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{cfg.desc}</p>
                </div>
                <Link to={cfg.to} className="inline-flex items-center gap-1 px-4 py-2 bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-bold rounded-full transition-colors" data-testid="seller-app-panel-cta">
                  {cfg.cta} <ChevronRight size={12} />
                </Link>
              </div>
            );
          })()}
        </section>

        {/* ============= FOLLOWED SELLERS ============= */}
        <section data-testid="followed-sellers-section">
          <SectionHeader icon={Users} title="내가 팔로우한 셀러" subtitle={`${following.length}명의 셀러를 팔로우 중`} />
          {following.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {following.map((s) => {
                const liveToday = isSellerLiveToday(s.seller_id);
                const liveNow = isSellerLiveNow(s.seller_id);
                return (
                  <div key={s.seller_id} className="flex items-center gap-3 p-4 bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200" data-testid="followed-seller-item">
                    <Link to={`/seller/${s.seller_id}`} className="relative flex-shrink-0" data-testid={`followed-seller-link-${s.seller_id}`}>
                      <SafeImg src={s.profile_photo} kind="seller_profile" alt={s.name} className="w-14 h-14 rounded-full object-cover" />
                      {liveNow && (
                        <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#FF3B30] border-2 border-white dark:border-[#141414] animate-pulse" data-testid="live-dot" />
                      )}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/seller/${s.seller_id}`} className="block">
                        <p className="font-bold text-sm truncate hover:text-[#FF3B30] transition-colors">{s.name}</p>
                      </Link>
                      {liveNow ? (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-bold rounded-full" data-testid="live-status-live">
                          <Radio size={10} /> LIVE
                        </span>
                      ) : liveToday ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="live-status-today">오늘 방송 예정</span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">방송 예정 없음</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Link
                        to={`/seller/${s.seller_id}`}
                        className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10 text-gray-500 hover:text-[#FF3B30] transition-colors"
                        title="알림 설정"
                        data-testid={`followed-notif-${s.seller_id}`}
                      >
                        <BellRing size={14} />
                      </Link>
                      <button
                        onClick={() => handleUnfollow(s.seller_id)}
                        className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10 text-gray-500 hover:text-[#FF3B30] transition-colors"
                        title="팔로우 취소"
                        data-testid={`unfollow-${s.seller_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 bg-black/5 dark:bg-white/5 rounded-lg" data-testid="no-followed-sellers">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">아직 팔로우한 셀러가 없습니다</p>
              <Link to="/" className="text-[#FF3B30] hover:underline text-sm font-bold">셀러 둘러보기</Link>
            </div>
          )}
        </section>

        {/* ============= BROADCAST NOTIFICATIONS ============= */}
        <section data-testid="broadcast-notifications-section">
          <SectionHeader icon={Bell} title="방송 알림" subtitle="각 알림을 개별적으로 켜거나 끌 수 있습니다" />
          <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg divide-y divide-black/5 dark:divide-white/10">
            {NOTIF_ROWS.map((r) => (
              <div key={r.key} className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="text-sm font-bold">{r.label}</p>
                </div>
                <Toggle
                  checked={!!globalPrefs[r.key]}
                  onChange={() => togglePref(r.key)}
                  testId={`global-notif-${r.key}`}
                />
              </div>
            ))}
          </div>
          {savingPrefs && <p className="text-xs text-gray-400 mt-2">저장 중...</p>}
        </section>

        {/* ============= MY SCHEDULE (followed only, buyers-only view) ============= */}
        {((applicationStatus?.seller_status || user?.seller_status) !== 'approved') && (
        <section data-testid="my-schedule-section">
          <SectionHeader icon={Calendar} title="내 방송 일정" subtitle="팔로우한 셀러의 방송만 표시됩니다" />
          <div className="flex gap-2 mb-4">
            {[
              { key: 'today',    label: '오늘' },
              { key: 'tomorrow', label: '내일' },
              { key: 'week',     label: '이번 주' }
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setScheduleTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                  scheduleTab === t.key
                    ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                    : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'
                }`}
                data-testid={`schedule-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tabbedSchedule.length > 0 ? (
            <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg divide-y divide-black/5 dark:divide-white/10" data-testid="my-schedule-list">
              {tabbedSchedule.map((l) => (
                <div key={l.schedule_id} className="flex items-center gap-4 p-4 hover:bg-black/5 dark:hover:bg-white/5 transition-colors" data-testid="my-schedule-row">
                  <SafeImg src={l.seller_photo} kind="avatar" alt={l.seller_name} className="w-10 h-10 rounded-full flex-shrink-0 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm line-clamp-1">{l.title}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span>{l.seller_name}</span>
                      <span>•</span>
                      <span className="capitalize">{l.platform}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-semibold text-[#FF3B30]">{format(new Date(l.scheduled_at), 'M월 d일', { locale: ko })}</p>
                    <p className="text-sm font-black">{format(new Date(l.scheduled_at), 'HH:mm', { locale: ko })}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-black/5 dark:bg-white/5 rounded-lg" data-testid="my-schedule-empty">
              <p className="text-sm text-gray-500 dark:text-gray-400">예정된 방송이 없습니다</p>
            </div>
          )}
        </section>
        )}

        {/* ============= BROADCAST FAVORITES ============= */}
        <section data-testid="favorites-section">
          <SectionHeader icon={Heart} title="즐겨찾기" subtitle="저장한 방송을 다시 볼 수 있습니다" />
          {broadcastFavorites.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="favorites-list">
              {broadcastFavorites.map((b) => (
                <div key={b.schedule_id} className="group bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-all duration-200" data-testid="favorite-item">
                  <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <SafeImg src={b.thumbnail} kind="broadcast" alt={b.title} className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleRemoveFavoriteBroadcast(b.schedule_id)}
                      className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-[#FF3B30] text-white rounded-full transition-colors"
                      data-testid={`remove-favorite-${b.schedule_id}`}
                      aria-label="즐겨찾기 삭제"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-bold line-clamp-2 mb-1">{b.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{b.seller_name}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-black/5 dark:bg-white/5 rounded-lg" data-testid="favorites-empty">
              <p className="text-sm text-gray-500 dark:text-gray-400">즐겨찾기한 방송이 없습니다</p>
            </div>
          )}
        </section>

        {/* ============= RECENTLY VIEWED ============= */}
        <section data-testid="recent-section">
          <SectionHeader
            icon={Clock}
            title="최근 본 방송"
            subtitle="최근에 확인한 방송이 자동으로 저장됩니다"
            action={recent.length > 0 ? (
              <button onClick={clearRecent} className="text-xs text-gray-500 hover:text-[#FF3B30] font-medium" data-testid="clear-recent">
                전체 삭제
              </button>
            ) : null}
          />
          {recent.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recent-list">
              {recent.slice(0, 8).map((r) => (
                <Link
                  key={r.schedule_id}
                  to={`/seller/${r.seller_id}`}
                  className="group bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-all duration-200"
                  data-testid="recent-item"
                >
                  <div className="aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <SafeImg src={r.thumbnail} kind="broadcast" alt={r.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-bold line-clamp-2 mb-1">{r.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{r.seller_name}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-black/5 dark:bg-white/5 rounded-lg" data-testid="recent-empty">
              <p className="text-sm text-gray-500 dark:text-gray-400">최근에 본 방송이 없습니다</p>
            </div>
          )}
        </section>

        {/* ============= INTERESTED CATEGORIES ============= */}
        <section data-testid="interested-categories-section">
          <SectionHeader icon={Sparkles} title="관심 카테고리" subtitle="선택한 카테고리를 기준으로 추천을 제공합니다" />
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((c) => {
              const active = interested.includes(c.value);
              return (
                <button
                  key={c.value}
                  onClick={() => toggleCategory(c.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5 ${
                    active
                      ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                      : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'
                  }`}
                  data-testid={`interest-${c.value}`}
                  aria-pressed={active}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={saveCategories}
            disabled={savingCategories}
            className="px-5 py-2 bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-50 text-white text-sm font-bold rounded-full transition-colors"
            data-testid="save-interests"
          >
            {savingCategories ? '저장 중...' : '저장'}
          </button>
        </section>

        {/* ============= NOTIFICATION CENTER ============= */}
        <section data-testid="notification-center-section">
          <SectionHeader icon={BellRing} title="알림센터" subtitle="최근 활동을 확인하세요" />
          <div className="flex flex-wrap gap-2 mb-4">
            {NOTIF_CENTER_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setNotifTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                  notifTab === t.key
                    ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                    : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'
                }`}
                data-testid={`notif-center-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filteredNotifs.length > 0 ? (
            <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg divide-y divide-black/5 dark:divide-white/10" data-testid="notification-feed">
              {filteredNotifs.map((n, idx) => (
                <div key={idx} className="flex items-start gap-3 px-5 py-4" data-testid="notification-feed-item">
                  <div className="w-9 h-9 rounded-full bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                    <Bell size={14} className="text-[#FF3B30]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">{n.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{n.subtitle}</p>
                  </div>
                  {n.live && (
                    <Link to={`/seller/${n.live.seller_id}`} className="text-xs text-[#FF3B30] font-bold hover:underline flex-shrink-0">
                      확인 <ChevronRight size={12} className="inline" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-black/5 dark:bg-white/5 rounded-lg" data-testid="notification-feed-empty">
              <p className="text-sm text-gray-500 dark:text-gray-400">알림이 없습니다</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MyPage;
